import {
    BarChart3,
    Bot,
    Calendar,
    FileText,
    LayoutDashboard,
    ListTodo,
    MessageSquare,
    Settings,
    Users,
} from "lucide-react";
import graphenLogo from "../../../image/Logo_graphen.svg";
import { HealthLang } from "../healthTypes";

const ICON_STROKE = 1.75;

const LABELS = {
    product: {
        En: "Aimma Doctor",
        Zh: "Aimma 醫師版",
        Jp: "Aimma 医師向け",
    },
    dashboard: {
        En: "Dashboard",
        Zh: "儀表板",
        Jp: "ダッシュボード",
    },
    greeting: {
        En: "Good morning, Dr. Priya Shah",
        Zh: "早安，Priya Shah 醫師",
        Jp: "おはようございます、Priya Shah 医師",
    },
    navDashboard: { En: "Dashboard", Zh: "儀表板", Jp: "ダッシュボード" },
    navAppointments: { En: "Appts", Zh: "預約", Jp: "予約" },
    navPatients: { En: "Patients", Zh: "病患", Jp: "患者" },
    navAgents: { En: "Agents", Zh: "代理", Jp: "エージェント" },
    navTasks: { En: "Tasks", Zh: "任務", Jp: "タスク" },
    navDocumentation: { En: "Docs", Zh: "文件", Jp: "文書" },
    navReports: { En: "Reports", Zh: "報告", Jp: "レポート" },
    navMessages: { En: "Messages", Zh: "訊息", Jp: "メッセージ" },
    navSettings: { En: "Settings", Zh: "設定", Jp: "設定" },
    tasksReview: { En: "Review", Zh: "待審", Jp: "確認" },
    todaysVisits: {
        En: "Today's Visits",
        Zh: "今日看診",
        Jp: "本日の診察",
    },
    todaysSchedule: {
        En: "Today's Schedule",
        Zh: "今日行程",
        Jp: "本日の予定",
    },
    previewAria: {
        En: "Physician dashboard interface preview",
        Zh: "醫師儀表板介面預覽",
        Jp: "医師ダッシュボード画面",
    },
} as const;

const SCHEDULE_ROWS = [
    { time: "9:00", name: "Margaret Johnson", mrn: "00234567", tone: "bg-teal-500" },
    { time: "9:30", name: "David Chen", mrn: "00345678", tone: "bg-sky-400" },
    { time: "10:00", name: "Sarah Williams", mrn: "00456789", tone: "bg-teal-400" },
    { time: "10:30", name: "James Wilson", mrn: "00567890", tone: "bg-amber-400" },
    { time: "11:00", name: "Maria Garcia", mrn: "00678901", tone: "bg-violet-400" },
] as const;

function t(key: keyof typeof LABELS, lang: HealthLang): string {
    return LABELS[key][lang] ?? LABELS[key].En;
}

interface DoctorDashboardMiniPreviewProps {
    lang: HealthLang;
}

const NAV_ITEMS = [
    { key: "navDashboard" as const, icon: LayoutDashboard, active: true },
    { key: "navAppointments" as const, icon: Calendar },
    { key: "navPatients" as const, icon: Users },
    { key: "navAgents" as const, icon: Bot },
    { key: "navDocumentation" as const, icon: FileText },
    { key: "navTasks" as const, icon: ListTodo, badge: "6" },
    { key: "navReports" as const, icon: BarChart3 },
    { key: "navMessages" as const, icon: MessageSquare, badge: "2" },
    { key: "navSettings" as const, icon: Settings },
] as const;

function SidebarNavItem({
    item,
    lang,
}: {
    item: (typeof NAV_ITEMS)[number];
    lang: HealthLang;
}) {
    const Icon = item.icon;
    const isActive = "active" in item && item.active;

    return (
        <div
            className={`flex w-full min-w-0 items-center justify-start gap-0.5 rounded px-0.5 py-0.5 text-left [text-align:left] ${
                isActive ? "bg-teal-50 text-teal-700" : "text-grey-500"
            }`}
        >
            <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                <Icon className="h-2.5 w-2.5" strokeWidth={ICON_STROKE} />
            </span>
            <span className="min-w-0 truncate text-left text-[7px] font-medium leading-tight [text-align:left]">
                {t(item.key, lang)}
            </span>
            {"badge" in item && item.badge ? (
                <span className="flex h-2.5 min-w-[0.65rem] shrink-0 items-center justify-center rounded-full bg-teal-600 px-0.5 text-[5px] font-bold leading-none text-white">
                    {item.badge}
                </span>
            ) : null}
        </div>
    );
}

export function DoctorDashboardMiniPreview({ lang }: DoctorDashboardMiniPreviewProps) {
    return (
        <div
            className="relative w-full min-w-[12rem] overflow-hidden rounded-lg border border-grey-100/90 bg-[#f4f8f9] text-left shadow-[0_8px_22px_rgba(16,24,40,0.1)] [text-align:left]"
            role="img"
            aria-label={t("previewAria", lang)}
        >
            <div className="flex min-h-[14rem] w-full">
                <aside
                    className="flex w-[5.5rem] shrink-0 flex-col border-r border-grey-100/90 bg-white/90 py-2 pl-1 pr-1 text-left [text-align:left]"
                    aria-hidden="true"
                >
                    <div className="mb-1.5 flex w-full min-w-0 items-center justify-start gap-0.5 px-0.5 text-left">
                        <img
                            src={graphenLogo}
                            alt=""
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0 object-contain"
                        />
                        <span className="min-w-0 flex-1 truncate text-left text-[7px] font-semibold leading-tight text-grey-800 [text-align:left]">
                            {t("product", lang)}
                        </span>
                    </div>
                    <nav className="w-full space-y-0.5 text-left [text-align:left]">
                        {NAV_ITEMS.map((item) => (
                            <SidebarNavItem key={item.key} item={item} lang={lang} />
                        ))}
                    </nav>
                </aside>

                <div
                    className="flex min-h-0 flex-1 flex-col p-2 text-left [text-align:left]"
                    aria-hidden="true"
                >
                    <p className="text-left text-[10px] font-bold leading-tight text-grey-900 [text-align:left]">
                        {t("dashboard", lang)}
                    </p>
                    <p className="mt-0.5 text-left text-[7px] leading-snug text-grey-500 [text-align:left]">
                        {t("greeting", lang)}
                    </p>

                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                        <div className="rounded-md border border-grey-100/90 bg-white px-1.5 py-1.5">
                            <div className="flex items-start justify-between gap-1">
                                <span className="text-[6.5px] font-medium text-grey-500">
                                    {t("todaysVisits", lang)}
                                </span>
                                <Calendar
                                    className="h-2.5 w-2.5 shrink-0 text-teal-500"
                                    strokeWidth={ICON_STROKE}
                                />
                            </div>
                            <p className="mt-1 text-left text-[11px] font-bold leading-none text-grey-900 [text-align:left]">
                                18
                            </p>
                        </div>
                        <div className="rounded-md border border-grey-100/90 bg-white px-1.5 py-1.5">
                            <div className="flex items-start justify-between gap-1">
                                <span className="text-left text-[6.5px] font-medium text-grey-500 [text-align:left]">
                                    {t("navTasks", lang)}
                                </span>
                                <ListTodo
                                    className="h-2.5 w-2.5 shrink-0 text-amber-500"
                                    strokeWidth={ICON_STROKE}
                                />
                            </div>
                            <p className="mt-0.5 text-[11px] font-bold leading-none text-grey-900">
                                6
                            </p>
                            <p className="mt-0.5 text-left text-[6.5px] font-medium text-amber-600 [text-align:left]">
                                {t("tasksReview", lang)}
                            </p>
                        </div>
                    </div>

                    <div className="mt-1.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-grey-100/90 bg-white p-1.5">
                        <p className="shrink-0 text-left text-[7px] font-semibold text-grey-800 [text-align:left]">
                            {t("todaysSchedule", lang)}
                        </p>
                        <ul className="mt-1 min-h-0 flex-1 space-y-1 overflow-hidden">
                            {SCHEDULE_ROWS.map((row) => (
                                <li
                                    key={row.time}
                                    className="flex items-center gap-1 border-t border-grey-50 pt-1 first:border-t-0 first:pt-0"
                                >
                                    <span className="w-5 shrink-0 text-[6.5px] font-medium text-grey-500">
                                        {row.time}
                                    </span>
                                    <span
                                        className={`h-1 w-1 shrink-0 rounded-full ${row.tone}`}
                                    />
                                    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-grey-100 text-[5px] font-semibold text-grey-600">
                                        {row.name
                                            .split(" ")
                                            .map((part) => part[0])
                                            .join("")}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="truncate text-[6.5px] font-semibold text-grey-800">
                                            {row.name}
                                        </p>
                                        <p className="truncate text-[6px] text-grey-400">
                                            MRN {row.mrn}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
